import { useMemo } from 'react';

import {
  AppliedPolicyStatusType,
  Environment,
} from '@/react/portainer/environments/types';
import { EnvironmentGroup } from '@/react/portainer/environments/environment-groups/types';

import { BadgeType } from '@@/Badge';

import { Policy } from '../../types';
import {
  getAppliedPolicyForType,
  isEnvironmentSupportedByPolicy,
} from '../../utils/isEnvironmentTypeSupportedByPolicy';

import { EnrichedPolicy, PolicyRollout } from './types';

export function usePoliciesWithEnvironmentGroups(
  policies: Policy[],
  groups: EnvironmentGroup[],
  environments: Environment[]
): EnrichedPolicy[] {
  return useMemo(
    () =>
      policies.map((policy) => ({
        ...policy,
        environmentGroups: computeEnvironmentGroups(
          policy,
          groups,
          environments
        ),
        status: computeStatus(policy),
        rollout: computeRollout(policy, environments),
      })),
    [policies, groups, environments]
  );
}

function computeEnvironmentGroups(
  policy: Policy,
  groups: EnvironmentGroup[],
  environments: Environment[]
) {
  if (policy.EnvironmentGroups?.length === 0) {
    return [];
  }

  return (
    groups
      .filter((group) => policy.EnvironmentGroups?.includes(group.Id))
      .map((group) => {
        const environmentsInGroup = environments.filter(
          (environment) => environment.GroupId === group.Id
        );
        const isEnvironmentGroupFullySupported = environmentsInGroup.every(
          (environment) =>
            isEnvironmentSupportedByPolicy(environment, policy.Type)
        );

        const status: BadgeType = isEnvironmentGroupFullySupported
          ? 'info'
          : 'warn';

        return {
          name: group.Name,
          id: group.Id,
          status,
          statusMessage: isEnvironmentGroupFullySupported
            ? ''
            : 'This environment group contains unsupported environment(s) for this policy',
        };
      })
      // show the env groups with unsupported environments for the policy first
      .sort((a, b) => {
        if (a.status === 'warn' && b.status !== 'warn') {
          return -1;
        }
        if (a.status !== 'warn' && b.status === 'warn') {
          return 1;
        }
        return 0;
      })
  );
}

function computeStatus(policy: Policy): 'Active' | 'Inactive' {
  const isActive =
    policy.EnvironmentGroups && policy.EnvironmentGroups.length > 0;
  return isActive ? 'Active' : 'Inactive';
}

function computeRollout(
  policy: Policy,
  environments: Environment[]
): PolicyRollout {
  const rollout: PolicyRollout = {
    applied: 0,
    pending: 0,
    failed: 0,
    notSupported: 0,
    total: 0,
  };

  // If no groups targeted, return zeros
  if (!policy.EnvironmentGroups?.length) {
    return rollout;
  }

  // Filter to targeted environments and classify each one
  const targetGroups = policy.EnvironmentGroups ?? [];
  environments
    .filter((env) => targetGroups.includes(env.GroupId))
    .forEach((env) => {
      if (!isEnvironmentSupportedByPolicy(env, policy.Type)) {
        rollout.notSupported++;
        return;
      }

      const appliedPolicy = getAppliedPolicyForType(env, policy.Type);

      // No policy object or no status → not applied yet → pending
      if (!appliedPolicy?.policyStatus) {
        rollout.pending++;
        return;
      }

      const status = appliedPolicy.policyStatus.Status;

      // Wrong policy version → pending
      if (appliedPolicy.policyID !== policy.Id) {
        rollout.pending++;
        return;
      }

      // Correct policy version, classify by status
      switch (status) {
        case AppliedPolicyStatusType.NotSupported:
          rollout.notSupported++;
          break;
        case AppliedPolicyStatusType.Applied:
          rollout.applied++;
          break;
        case AppliedPolicyStatusType.Failed:
          rollout.failed++;
          break;
        case AppliedPolicyStatusType.InProgress:
        case AppliedPolicyStatusType.Warning:
          rollout.pending++;
          break;
        default:
          // No action needed for unexpected status values
          break;
      }
    });

  rollout.total =
    rollout.applied + rollout.pending + rollout.failed + rollout.notSupported;
  return rollout;
}
